// 管理側：有給休暇の代理申請
// 社員が期限内に申請できなかった場合などに、申請管理の権限を持つ人が代わりに登録する。
// 登録は「申請中」で作られ、承認は通常の申請一覧から行う（誰が承認したかの記録を残すため）。
import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import "./ProxyLeaveApplyModal.css";

const REASON_OPTIONS = ["病欠", "私用のため", "欠勤", "その他"];

/** ローカル日付を YYYY-MM-DD にする（toISOString だと UTC になり日付がずれる） */
const toYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const ProxyLeaveApplyModal = ({ onClose, onCreated }) => {
  const [employees, setEmployees] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [memberId, setMemberId] = useState("");
  const [dateInput, setDateInput] = useState(toYmd(new Date()));
  const [dates, setDates] = useState([]);
  const [reasonType, setReasonType] = useState("");
  const [reasonOther, setReasonOther] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/applications/proxy-candidates/")
      .then((res) => {
        if (!alive) return;
        setEmployees(Array.isArray(res.data?.employees) ? res.data.employees : []);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err.response?.status === 403
            ? "代理申請を行う権限がありません。"
            : "社員一覧の取得に失敗しました。"
        );
      });
    return () => {
      alive = false;
    };
  }, []);

  const selected = useMemo(
    () => employees.find((e) => e.memberId === memberId) || null,
    [employees, memberId]
  );

  const addDate = () => {
    if (!dateInput) return;
    setDates((prev) => (prev.includes(dateInput) ? prev : [...prev, dateInput].sort()));
  };

  const removeDate = (d) => setDates((prev) => prev.filter((x) => x !== d));

  const canSubmit =
    memberId && dates.length > 0 && reasonType && (reasonType !== "その他" || reasonOther.trim());

  const handleSubmit = async () => {
    if (!canSubmit || saving) return;

    // 残日数が足りなくても登録は止めない（特例対応・前借りがあるため）。確認だけ出す。
    const remain = selected?.leaveRemain;
    if (typeof remain === "number" && dates.length > remain) {
      const ok = window.confirm(
        `${selected.name} さんの有給残日数は ${remain} 日です。\n` +
          `${dates.length} 日を申請しようとしています。\n\nこのまま登録しますか？`
      );
      if (!ok) return;
    }

    setSaving(true);
    setMessage("");
    try {
      await api.post("/forms/submit_paid_leave/", {
        memberId,
        selectedDates: dates,
        reason: reasonType === "その他" ? reasonOther.trim() : reasonType,
      });
      onCreated?.();
      onClose?.();
    } catch (err) {
      setMessage(
        err.response?.data?.detail ||
          (typeof err.response?.data === "string" ? err.response.data : "") ||
          "登録に失敗しました。"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="plm-overlay" onClick={onClose}>
      <div className="plm-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="plm-title">有給休暇の代理申請</h3>
        <p className="plm-note">
          社員に代わって有給休暇を登録します。申請期限の設定に関わらず登録できます。
          登録後は「申請中」として一覧に並ぶので、内容を確認して承認してください。
        </p>

        {loadError ? (
          <p className="plm-error">{loadError}</p>
        ) : (
          <>
            <label className="plm-label">
              社員
              <select
                className="plm-input"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
              >
                <option value="">-- 選択 --</option>
                {employees.map((e) => (
                  <option key={e.memberId} value={e.memberId}>
                    {e.memberId} {e.name}
                    {e.departmentName ? `（${e.departmentName}）` : ""}
                  </option>
                ))}
              </select>
            </label>

            {selected && (
              <p className="plm-remain">
                有給残日数：
                <strong>
                  {typeof selected.leaveRemain === "number" ? `${selected.leaveRemain} 日` : "—"}
                </strong>
                {typeof selected.leaveRemain === "number" &&
                  dates.length > selected.leaveRemain && (
                    <span className="plm-warn">　※ 申請日数が残日数を超えています</span>
                  )}
              </p>
            )}

            <label className="plm-label">
              取得日（1日ずつ追加）
              <span className="plm-row">
                <input
                  type="date"
                  className="plm-input"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                />
                <button type="button" className="plm-btn-add" onClick={addDate}>
                  追加
                </button>
              </span>
            </label>

            {dates.length > 0 && (
              <ul className="plm-dates">
                {dates.map((d) => (
                  <li key={d}>
                    {d}
                    <button type="button" onClick={() => removeDate(d)} aria-label={`${d} を削除`}>
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <label className="plm-label">
              理由
              <select
                className="plm-input"
                value={reasonType}
                onChange={(e) => setReasonType(e.target.value)}
              >
                <option value="">-- 選択 --</option>
                {REASON_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>

            {reasonType === "その他" && (
              <label className="plm-label">
                その他の理由（30文字まで）
                <input
                  type="text"
                  className="plm-input"
                  maxLength={30}
                  value={reasonOther}
                  onChange={(e) => setReasonOther(e.target.value)}
                />
              </label>
            )}

            {message && <p className="plm-error">{message}</p>}

            <div className="plm-actions">
              <button type="button" className="plm-btn-cancel" onClick={onClose} disabled={saving}>
                キャンセル
              </button>
              <button
                type="button"
                className="plm-btn-save"
                onClick={handleSubmit}
                disabled={!canSubmit || saving}
              >
                {saving ? "登録中…" : "申請中で登録"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProxyLeaveApplyModal;
